<template>
	<div class="p-p-2 child-content">
		<TranslationListView
			v-if="masterDataTranslationFilter"
			:key="masterDataTranslationFilter"
			:fixedFilter="masterDataTranslationFilter"
			:allowedKeys="allowedTranslationKeys"
			disabledNewEntryButton
			@keysLoaded="onKeysLoaded"
		/>
	</div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {MasterDataColumn, MasterDataTable} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {ToastManager} from "@/util/ToastManager";
import TranslationListView from "@/apps/administration/views/squeeze/system/TranslationListView.vue";

export default defineComponent({
	name: "MasterDataTranslationsView",
	components: {
		TranslationListView,
	},
	props: {
		masterDataTableId: {
			type: Number,
			default: 0,
		},
	},
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();

		/** Api for master data; only the name is needed for the fixed translation filter */
		const masterDataApi = ClientManager.getInstance().squeeze.masterData;

		/** Current master data table; only the name is needed for the fixed translation filter */
		const masterDataTable = reactive<MasterDataTable>({
			name: '',
			description: '',
		});

		/** All translation keys loaded by the TranslationListView */
		const loadedTranslationKeys = ref<string[]>([]);

		/** Translation keys of columns without activated translation */
		const excludedTranslationKeys = ref<string[]>([]);

		/** Fixed translations filter for all keys that belong to the current master data table */
		const masterDataTranslationFilter = computed<string>(() => {
			return masterDataTable.name ? `masterdata.${masterDataTable.name}.*` : '';
		});

		/**
		 * Allow-list of visible keys: all loaded keys
		 * only the manually toggleable column translations are filtered out.
		 */
		const allowedTranslationKeys = computed<string[]>(() => {
			return loadedTranslationKeys.value.filter((key: string) =>
				!excludedTranslationKeys.value.some((excludedKey: string) => key === excludedKey || key.startsWith(excludedKey + "."))
			);
		});

		/** Gets the master data table to derive the fixed translations filter */
		const getMasterDataTable = () => {
			if (!props.masterDataTableId) {
				return;
			}

			masterDataApi.getMasterDataTableById(props.masterDataTableId)
				.then((data: MasterDataTable) => {
					Object.assign(masterDataTable, data);
				})
				.catch((err: { statusText: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.MasterData.MasterData') + ": " + err.statusText);
				});
		};

		/** Stores all keys emitted by the TranslationListView after it loaded the translations */
		const onKeysLoaded = (keys: string[]) => {
			loadedTranslationKeys.value = keys;
		};

		/** Collects the translation keys of the columns without activated translation */
		const getExcludedTranslationKeys = () => {
			if (!props.masterDataTableId) {
				excludedTranslationKeys.value = [];
				return;
			}

			masterDataApi.getMasterDataTableColumns(props.masterDataTableId)
				.then((data: MasterDataColumn[]) => {
					excludedTranslationKeys.value = (data ?? [])
						.filter((column: MasterDataColumn) => column.translatable !== true && !!column.translationKey)
						.map((column: MasterDataColumn) => column.translationKey as string);
				})
				.catch((err: { statusText: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.MasterData.MasterData') + ": " + err.statusText);
				});
		};

		/** Loads table name and excluded translation keys */
		const reloadData = () => {
			getMasterDataTable();
			getExcludedTranslationKeys();
		};

		onMounted(() => {
			reloadData();
		});

		watch(() => props.masterDataTableId, () => {
			reloadData();
		});

		return {
			masterDataTranslationFilter,
			allowedTranslationKeys,
			onKeysLoaded,
		};
	},
});
</script>

<style scoped>

.child-content {
	height: calc(100vh - 5.6rem - 3.538rem);
}

</style>
